import { ApolloClient, InMemoryCache, NormalizedCacheObject, from, HttpLink, ApolloLink } from '@apollo/client';
import { RetryLink } from '@apollo/client/link/retry';
import { onError } from '@apollo/client/link/error';
import { Kind } from 'graphql';
import NotificationManager from "components/common/react-notifications/NotificationManager";
import availableLanguages from 'lang/availableLanguages';

// Track retry attempts for each operation
const retryTracker = new Map();
let networkRetryNotification: any = null;
let networkRetryNotificationTimer: number | null = null;
let networkRetryNotificationsActive = false;
let networkRetryHadFailure = false;
let backendHealthCheckStarted = false;
let backendHealthCheckInFlight = false;
let backendHealthCheckFailures = 0;
let connectionRestoredNotificationShown = false;
let recoveryRefetchInFlight = false;
let mainApolloClient: ApolloClient<NormalizedCacheObject> | null = null;

const BACKEND_HEALTH_CHECK_INTERVAL_MS = 10000;
const BACKEND_HEALTH_CHECK_TIMEOUT_MS = 8000;
const BACKEND_HEALTH_CHECK_FAILURES_BEFORE_NOTIFY = 3;

const getUiMessage = (id: string) => {
  const locale = typeof window !== 'undefined' ? window.localStorage.getItem('locale') || 'en' : 'en';
  return availableLanguages[locale]?.[id]?.defaultMessage || availableLanguages.en?.[id]?.defaultMessage || id;
};

const clearNetworkRetryNotificationTimer = () => {
  if (networkRetryNotificationTimer !== null) {
    window.clearTimeout(networkRetryNotificationTimer);
    networkRetryNotificationTimer = null;
  }
};

const removeNetworkRetryNotification = () => {
  if (networkRetryNotification) {
    NotificationManager.remove(networkRetryNotification);
    networkRetryNotification = null;
  }
};

const stopNetworkRetryNotifications = () => {
  networkRetryNotificationsActive = false;
  clearNetworkRetryNotificationTimer();
  removeNetworkRetryNotification();
};

const refetchActiveQueriesAfterRecovery = () => {
  if (!mainApolloClient || recoveryRefetchInFlight) {
    return;
  }

  recoveryRefetchInFlight = true;
  mainApolloClient.refetchQueries({ include: 'active' })
    .catch((error) => {
      console.error('Failed to refetch active queries after backend recovery:', error);
    })
    .finally(() => {
      recoveryRefetchInFlight = false;
    });
};

const startNetworkRetryNotifications = () => {
  networkRetryHadFailure = true;

  if (networkRetryNotificationsActive) {
    return;
  }

  networkRetryNotificationsActive = true;

  const showNotification = () => {
    if (!networkRetryNotificationsActive) {
      return;
    }

    removeNetworkRetryNotification();
    networkRetryNotification = NotificationManager.warning(
      getUiMessage('network.problem-retrying'),
      null,
      3000,
      null,
      null,
      'filled'
    );

    networkRetryNotificationTimer = window.setTimeout(() => {
      removeNetworkRetryNotification();
      networkRetryNotificationTimer = window.setTimeout(showNotification, 500);
    }, 3000);
  };

  showNotification();
};

const showConnectionRestoredNotification = () => {
  if (!networkRetryHadFailure) {
    return;
  }

  stopNetworkRetryNotifications();
  networkRetryHadFailure = false;
  refetchActiveQueriesAfterRecovery();

  if (!connectionRestoredNotificationShown) {
    connectionRestoredNotificationShown = true;
    NotificationManager.success(
      getUiMessage('network.connection-restored'),
      getUiMessage('general.success'),
      3000,
      null,
      null,
      'filled'
    );
    setTimeout(() => {
      connectionRestoredNotificationShown = false;
    }, 5000);
  }
};

const startBackendHealthCheck = (url: string) => {
  if (backendHealthCheckStarted || typeof window === 'undefined') {
    return;
  }

  backendHealthCheckStarted = true;

  window.setInterval(async () => {
    if (backendHealthCheckInFlight) {
      return;
    }

    backendHealthCheckInFlight = true;
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), BACKEND_HEALTH_CHECK_TIMEOUT_MS);

    try {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        signal: controller.signal,
        headers: {
          'content-type': 'application/json',
        },
        body: JSON.stringify({ query: 'query BackendHealthCheck { __typename }' }),
      });

      if (response.ok) {
        backendHealthCheckFailures = 0;
        showConnectionRestoredNotification();
      } else if (response.status >= 500) {
        backendHealthCheckFailures += 1;
        if (backendHealthCheckFailures >= BACKEND_HEALTH_CHECK_FAILURES_BEFORE_NOTIFY) {
          startNetworkRetryNotifications();
        }
      }
    } catch {
      backendHealthCheckFailures += 1;
      if (backendHealthCheckFailures >= BACKEND_HEALTH_CHECK_FAILURES_BEFORE_NOTIFY) {
        startNetworkRetryNotifications();
      }
    } finally {
      window.clearTimeout(timeoutId);
      backendHealthCheckInFlight = false;
    }
  }, BACKEND_HEALTH_CHECK_INTERVAL_MS);

};

// Create a function to generate a retry link with consistent configuration
const createRetryLink = (isEcoregion = false) => {
  return new RetryLink({
    delay: {
      initial: 1000, // Initial delay in ms
      max: 5000,     // Maximum delay in ms
      jitter: true   // Randomize delay to prevent thundering herd
    },
    attempts: {
      max: isEcoregion ? 5 : 10, // Different max attempts based on client
      retryIf: (error, operation) => {
        // Retry on network errors or server errors (5xx) for both queries and mutations
        const operationType = operation.operationName ? operation.operationName : 'unknown operation';
        const isNetworkError = !error.result && error.networkError;
        const isServerError = error.statusCode >= 500;

        // Safely determine the operation type (query, mutation, subscription)
        let operationKind = 'unknown';
        const definition = operation.query.definitions[0];
        if (definition && definition.kind === Kind.OPERATION_DEFINITION) {
          operationKind = definition.operation;
        }

        // Track retry attempts for this operation
        const operationKey = `${isEcoregion ? 'eco-' : ''}${operation.operationName || 'unknown'}-${Date.now()}`;
        if (!retryTracker.has(operationKey)) {
          retryTracker.set(operationKey, { 
            count: 0, 
            operationName: operation.operationName,
            operationType: operationKind,
            isEcoregion
          });
        }
        
        const trackerInfo = retryTracker.get(operationKey);
        trackerInfo.count += 1;
        retryTracker.set(operationKey, trackerInfo);

        // Log retry attempts
        if (isNetworkError || isServerError) {
          console.error(`Retrying ${isEcoregion ? 'bioregion ' : ''}${operationType} due to error (attempt ${trackerInfo.count}):`, error);
          // Only show notification for main client to avoid duplicate notifications
          if (!isEcoregion) {
            startNetworkRetryNotifications();
          }
          return true;
        }
        return false;
      }
    }
  });
};

// Create an "afterware" link to handle successful operations
const createAfterwareLink = (isEcoregion = false) => {
  return new ApolloLink((operation, forward) => {
    return forward(operation).map(response => {
      if (!isEcoregion && networkRetryHadFailure) {
        showConnectionRestoredNotification();
      }

      // Check if this operation was retried
      const operationName = operation.operationName || 'unknown';
      
      // Find the tracker entry for this operation
      let trackerEntry = null;
      for (const [key, value] of retryTracker.entries()) {
        if (value.operationName === operationName) {
          trackerEntry = value;
          retryTracker.delete(key); // Clean up the tracker
          break;
        }
      }
      
      // If this operation was retried and now succeeded
      if (!isEcoregion && trackerEntry && trackerEntry.count > 0) {
        stopNetworkRetryNotifications();
        
       // Clean up stale retryTracker entries for this operation to prevent leaks
        for (const [key, value] of Array.from(retryTracker.entries())) {
          if (value.operationName === operationName) {
            retryTracker.delete(key);
          }
        }

      }
      
      return response;
    });
  });
};

// Create the error link
const errorLink = onError(({ operation, forward }) => {
  return forward(operation);
});

// Create the main Apollo client
const graphqlURL = import.meta.env.VITE_GRAPHQL || 'http://localhost:3000/graphql';
startBackendHealthCheck(graphqlURL);

const httpLink = new HttpLink({
  uri: graphqlURL,
  credentials: 'include',
});

export const apolloClient: ApolloClient<NormalizedCacheObject> = new ApolloClient({
  link: from([
    errorLink, 
    createAfterwareLink(), 
    createRetryLink(), 
    httpLink
  ]),
  connectToDevTools: true,
  cache: new InMemoryCache({
    typePolicies: {
      EventCourse: {
        fields: {
          agenda: {
            // Somehow Apollo cache also cares about the content stored inside of the agenda field, even thou it is an array stored in the database.
            // This way we tell it to always replace the old content of agenda queried on the same EventCourse.
            // https://www.apollographql.com/docs/react/caching/cache-field-behavior/#merging-non-normalized-objects
            merge: false,
          },
        },
      },
    },
  }),
  defaultOptions: {
    query: {
      fetchPolicy: 'cache-first',
    },
    watchQuery: {
      nextFetchPolicy: 'cache-and-network',
    },
  },
});
mainApolloClient = apolloClient;

// Create the ecoregion Apollo client
const bioregionGraphqlURL = import.meta.env.VITE_BIOREGION_GRAPHQL || 'http://localhost:3000/bioregion-graphql';
const bioregionHttpLink = new HttpLink({
  uri: bioregionGraphqlURL,
});

export const ecoregionApolloClient: ApolloClient<NormalizedCacheObject> = new ApolloClient({
  link: from([
    errorLink, 
    createAfterwareLink(true), 
    createRetryLink(true), // Pass true to indicate this is the ecoregion client
    bioregionHttpLink
  ]),
  cache: new InMemoryCache(),
  defaultOptions: {
    query: {
      fetchPolicy: 'cache-first',
    },
  },
});
